import {clamp, MiniSignal, random, spine} from "p3";
import definitions from "../../../definitions";
import GameScene from "../../../scenes/GameScene";
import Enemy from "./Enemy";

export default class EnemySwanling extends Enemy {
    constructor() {
        super(definitions.ENEMY_ID.SWANLING);

        this.moveSpeed = 1000;
        this.linearDamping = 6;
        this.collisionDamping = 12;

        this.collisionRadius = 12;
        this.collisionOffsetX = -5;
        this.collisionOffsetY = -12;

        this.moveToTarget = null;
        this.moveToTargetOffset = new PIXI.Point();
        this.moveToTargetMinRadius = 20;
        this.moveToTargetMaxRadius = 100;

        this.moveAwayFromTarget = null;
        this.moveAwayFromTargetOffset = new PIXI.Point();
        this.moveAwayFromTargetMinRadius = 0;
        this.moveAwayFromTargetMaxRadius = 600;

        this._avoidNeighbourMinRange = 140;
        this._avoidNeighbourWeight = 0.6;

        this._wanderTime = 0;
        this._wanderDelay = 0;
        this._wanderDelayMin = 0.5;
        this._wanderDelayMax = 2;
        this._wanderSpeed = 600;

        this._followSpeed = 2700;

        this._defaultTween = null;

        this.interactable = false;
        this.interactionRadius = 80;

        this.signals.grabbed = new MiniSignal();
    }

    init() {
        super.init();

        this.addViewWithSpine(spine("char_swanling"));
        
        this._view.stateData.defaultMix = 0.1;
        this._view.scale.set(0.245);

        this._playIdleAnimation();
    }

    onViewAdded() {
        super.onViewAdded();

        this.addShadow(definitions.SHADOW_SIZE.SMALL, 1, new PIXI.Point(5, 8));
    }

    destroy() {
        this._stopAnimations();
        
        this._view.state.removeListener(this._spineListener);

        super.destroy();
    }

    takeDamage(amount) {
        return false;
    }

    update() {
        super.update();
        
        this._view.update(window.dt);

        if (this.velocity.lengthSq > 4 ** 2) {
            if (!this._isMoving) {
                this._isMoving = true;
                this.onMoveStart();
            }
        } else {
            if (this._isMoving) {
                this._isMoving = false;
                this.onMoveStop();
            }
        }
    }

    _playIdleAnimation() {
        this._view.state.setAnimation(0, "idle_00", true);
    }

    /**
     * @return {PIXI.Point}
     * @private
     */
    _findWanderPosition() {
        return this.world.findSafePosition(
            new PIXI.Point(this._initialPosition.x, this._initialPosition.y),
            200,
            0,
            0,
            [new PIXI.Point(this.x, this.y)],
            20
        );
    }

    /**
     * @param {object} target
     * @param {P3.Vector2} force
     * @private
     */
     _moveToTarget(target, force) {
        let move = true;

        force.set(target.x - this.x, target.y - this.y);
        const distance = force.length;
        force.x /= distance;
        force.y /= distance;

        let targetSpeed = Math.min(1, (distance / this.moveToTargetMaxRadius)) * this.moveSpeed;
        if (distance < this.moveToTargetMinRadius) {
            move = false;
            targetSpeed = 0;
        }
        force.scaleBy(targetSpeed);
        return move;
    }

        /**
     * @param {object} target
     * @param {P3.Vector2} force
     * @private
     */
    _moveAwayFromTarget(target, force) {
        let move = true;

        force.set(this.x - target.x, this.y - target.y);
        const distance = force.length;
        //const inverse = Math.min(this.moveAwayFromTargetMaxRadius, Math.max(this.moveAwayFromTargetMinRadius, this.moveAwayFromTargetMaxRadius - force.length));
        force.x /= distance;
        force.y /= distance;

        let targetSpeed = Math.max(0, Math.min(1, 1-(distance / this.moveAwayFromTargetMaxRadius))) * this.moveSpeed;
        if (distance < this.moveAwayFromTargetMinRadius) {
            move = false;
            targetSpeed = 0;
        }
        force.scaleBy(targetSpeed);
        return move;
    }

    /**
     * @param {P3.Vector2} force
     * @param {number} minRange
     * @param {number} [weight = 0.5]
     * @private
     */
    _avoidNeighbours(force, minRange, weight = 0.5) {
        weight = Math.max(0, weight);
        for (let i = 0, count = this.neighbours.length; i < count; ++i) {
            const neighbour = this.neighbours[i];
            if (this !== neighbour) {
                if (!(this.x === neighbour.x && this.y === neighbour.y)) {
                    const to = new P3.Vector2(this.x - neighbour.x, this.y - neighbour.y);
                    const distance = to.length;
                    if (distance < minRange) {
                        const speed = (1 - clamp(distance / minRange, 0, 1)) * minRange;
                        to.x /= distance;
                        to.y /= distance;
                        force.x += to.x * speed * weight;
                        force.y += to.y * speed * weight;
                    }
                }
            }
        }
    }

    _stopAnimations() {
        if (this._defaultTween) {
            GameScene.active.removeTween(this._defaultTween, true);
            this._defaultTween = null;
        }
    }

    
    onMoveStart() {
        this._view.state.setAnimation(0, "walk_r_00", true);
    }

    onMoveStop() {
        this._playIdleAnimation();
    }

    /**
     * @param {Entity} entity
     * @param {P3.Vector2} v
     */
    onCollisionEntity(entity, v) {
        super.onCollisionEntity(entity, v);

        this.x += v.x;
        this.y += v.y;

        v.normalize();

        const d = 1 - Math.exp(-this.collisionDamping * window.dt);

        this.velocity.x *= 1 - Math.abs(v.x) * d;
        this.velocity.y *= 1 - Math.abs(v.y) * d;
    }

    /**
     * @param {Tile} tile
     * @param {P3.Vector2} v
     */
    onCollisionTile(tile, v) {
        super.onCollisionTile(tile, v);

        this.x += v.x;
        this.y += v.y;

        v.normalize();

        const d = 1 - Math.exp(-this.collisionDamping * window.dt);

        this.velocity.x *= 1 - Math.abs(v.x) * d;
        this.velocity.y *= 1 - Math.abs(v.y) * d;
    }

    /**
     * @param {number} state
     * @private
     */
    
    onStateEnter(state) {
        switch (state) {

            case definitions.ENEMY_STATE.WANDER: {
                //console.log('set wander');
                this._wanderTime = 0;
                this._wanderDelay = random(this._wanderDelayMin, this._wanderDelayMax);

                this.moveSpeed = this._wanderSpeed;
                this.moveToTarget = this._findWanderPosition();
                break;
            }
            case definitions.ENEMY_STATE.CAPTURE: {
                this.moveSpeed = this._followSpeed;
            }
        }
    }

        /**
     * @param {number} state
     */
    onStateUpdate(state) {

        switch (state) {

            case definitions.ENEMY_STATE.WANDER: {
                if (this.moveToTarget) {
                    if (this._wanderTime > this._wanderDelay) {
                        this.state = definitions.ENEMY_STATE.WANDER;
                    }
                    this._wanderTime += window.dt;
                }
                const d = new P3.Vector2(this.x - this.target.x, this.y - this.target.y);
                if (d.length < 50) {
                    this.signals.grabbed.dispatch(this);
                }

                break;
            }
            case definitions.ENEMY_STATE.CAPTURE: {
                
                break;
            }
        }

        if (this.moveToTarget) {
            const force = new P3.Vector2();
            const target = new PIXI.Point(this.moveToTarget.x + this.moveToTargetOffset.x, this.moveToTarget.y + this.moveToTargetOffset.y);

            this._moveToTarget(target, force);
            this.facingDirection = force.x;

            this._avoidNeighbours(force, this._avoidNeighbourMinRange, this._avoidNeighbourWeight);
            this.velocity.set(this.velocity.x + force.x * window.dt, this.velocity.y + force.y * window.dt);
        }
    }
}